import { http, HttpResponse } from 'msw';
import { mockOrganizations, findOrganizationById, Organization } from '../data/organizations';
import { PaginationResponse } from '../../types/api';

// Helper function to create error responses
const createErrorResponse = (message: string, code: number, status: number) => {
  return HttpResponse.json({
    success: false,
    data: null,
    message,
    code
  }, { status });
};

// Helper function to extract user ID from token（开发环境兼容真实 JWT：只要存在 Bearer 即放行）
const getUserIdFromToken = (authHeader: string | null): string | null => {
  if (!authHeader || !authHeader.startsWith('Bearer ')) {
    return null;
  }
  // 兼容原有 mock token 解析，同时对非匹配格式（真实 JWT 等）默认放行
  const token = authHeader.substring(7);
  const tokenMatch = token.match(/mock-jwt-token-(\d+)-/);
  if (tokenMatch) return tokenMatch[1];
  // 开发环境下：任意非空 Bearer token 视为有效（返回一个固定用户ID）
  return '1';
};

// Helper function to paginate results
const paginateResults = <T>(
  items: T[],
  page: number = 1,
  pageSize: number = 10
): { items: T[]; pagination: PaginationResponse } => {
  const startIndex = (page - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedItems = items.slice(startIndex, endIndex);
  
  return {
    items: paginatedItems,
    pagination: {
      page,
      pageSize,
      total: items.length,
      totalPages: Math.ceil(items.length / pageSize)
    }
  };
};

export const organizationHandlers = [
  // Get organizations list
  http.get('/api/v1/organizations', async ({ request }) => {
    try {
      const authHeader = request.headers.get('Authorization');
      const userId = getUserIdFromToken(authHeader);
      
      if (!userId) {
        return createErrorResponse('Unauthorized', 401, 401);
      }
      
      const url = new URL(request.url);
      const page = parseInt(url.searchParams.get('page') || '1');
      const pageSize = parseInt(url.searchParams.get('pageSize') || '10');
      const search = url.searchParams.get('search') || '';
      const status = url.searchParams.get('status') || '';
      
      // Filter organizations based on search and status
      let filteredOrgs = mockOrganizations;
      
      if (search) {
        filteredOrgs = filteredOrgs.filter(org => 
          org.name.toLowerCase().includes(search.toLowerCase()) ||
          org.description?.toLowerCase().includes(search.toLowerCase())
        );
      }
      
      if (status) {
        filteredOrgs = filteredOrgs.filter(org => org.status === status);
      }
      
      const result = paginateResults(filteredOrgs, page, pageSize);
      
      // Simulate network delay
      await new Promise(resolve => setTimeout(resolve, 500));
      
      return HttpResponse.json({
        success: true,
        data: {
          organizations: result.items,
          pagination: result.pagination
        },
        message: 'Organizations retrieved successfully'
      });
      
    } catch (error) {
      return createErrorResponse('Failed to retrieve organizations', 500, 500);
    }
  }),

  // Get organization by ID
  http.get('/api/v1/organizations/:organizationId', async ({ request, params }) => {
    try {
      const authHeader = request.headers.get('Authorization');
      const userId = getUserIdFromToken(authHeader);
      
      if (!userId) {
        return createErrorResponse('Unauthorized', 401, 401);
      }
      
      const { organizationId } = params;
      const organization = findOrganizationById(organizationId as string);
      
      if (!organization) {
        return createErrorResponse('Organization not found', 404, 404);
      }
      
      // Simulate network delay
      await new Promise(resolve => setTimeout(resolve, 400));
      
      return HttpResponse.json({
        success: true,
        data: organization,
        message: 'Organization details retrieved successfully'
      });
      
    } catch (error) {
      return createErrorResponse('Failed to retrieve organization details', 500, 500);
    }
  }),

  // Create organization
  http.post('/api/v1/organizations', async ({ request }) => {
    try {
      const authHeader = request.headers.get('Authorization');
      const userId = getUserIdFromToken(authHeader);
      
      if (!userId) {
        return createErrorResponse('Unauthorized', 401, 401);
      }
      // 兼容 multipart/form-data 与 application/json 两种提交方式
      let orgData: Partial<Organization> = {};
      const contentType = request.headers.get('Content-Type') || '';
      if (contentType.includes('multipart/form-data')) {
        const fd = await (request as Request).formData();
        orgData = {
          name: String(fd.get('name') || ''),
          description: fd.get('description') ? String(fd.get('description')) : undefined,
        };
      } else {
        try {
          orgData = (await (request as Request).json()) as Partial<Organization>;
        } catch {
          orgData = {};
        }
      }
      
      // Create new organization
      const newOrganization: Organization = {
        id: `org-${Date.now()}`,
        name: orgData.name || '',
        description: orgData.description,
        status: 'active',
        memberCount: 1,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      
      // Simulate network delay
      await new Promise(resolve => setTimeout(resolve, 800));
      
      return HttpResponse.json({
        success: true,
        data: newOrganization,
        message: 'Organization created successfully'
      }, { status: 201 });
      
    } catch (error) {
      return createErrorResponse('Failed to create organization', 500, 500);
    }
  }),

  // Update organization
  http.put('/api/v1/organizations/:organizationId', async ({ request, params }) => {
    try {
      const authHeader = request.headers.get('Authorization');
      const userId = getUserIdFromToken(authHeader);
      
      if (!userId) {
        return createErrorResponse('Unauthorized', 401, 401);
      }
      
      const { organizationId } = params;
      const organization = findOrganizationById(organizationId as string);
      
      if (!organization) {
        return createErrorResponse('Organization not found', 404, 404);
      }
      
      const updateData = await request.json() as Partial<Organization>;
      
      const updatedOrganization: Organization = {
        ...organization,
        ...updateData,
        id: organization.id, // Prevent ID from being changed
        updatedAt: new Date().toISOString()
      };
      
      // Simulate network delay
      await new Promise(resolve => setTimeout(resolve, 600));
      
      return HttpResponse.json({
        success: true,
        data: updatedOrganization,
        message: 'Organization updated successfully'
      });
      
    } catch (error) {
      return createErrorResponse('Failed to update organization', 500, 500);
    }
  }),

  // Delete organization
  http.delete('/api/v1/organizations/:organizationId', async ({ request, params }) => {
    try {
      const authHeader = request.headers.get('Authorization');
      const userId = getUserIdFromToken(authHeader);
      
      if (!userId) {
        return createErrorResponse('Unauthorized', 401, 401);
      }
      
      const { organizationId } = params;
      const organization = findOrganizationById(organizationId as string);
      
      if (!organization) {
        return createErrorResponse('Organization not found', 404, 404);
      }
      
      // Simulate network delay
      await new Promise(resolve => setTimeout(resolve, 700));
      
      return HttpResponse.json({
        success: true,
        data: null,
        message: 'Organization deleted successfully'
      });
      
    } catch (error) {
      return createErrorResponse('Failed to delete organization', 500, 500);
    }
  })
];